import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  ServerGetRequestFunction,
  useServerRequests,
} from '~/hooks/useRequests'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'

import type { NFTCollectionMetadataLookup } from '~/components/txn-display/state-change/types'

type CollectionMetadataTarget = {
  chainId: SupportedChainId
  contractAddress: IAddress
}

type BackendContractMetadataResponse = {
  contracts: Array<{
    chainId: number
    address: string
    name: string | null
  }>
}

export const getCollectionMetadataLookupKey = (
  chainId: SupportedChainId,
  address: IAddress,
): string => `${chainId}-${address.toLowerCase()}`

/**
 * Build a stable query key based on the sorted list of contracts.
 * This ensures the same set of contracts always produces the same cache key.
 */
const buildBatchQueryKey = (targets: CollectionMetadataTarget[]) => {
  const sortedTargets = [...targets].sort((a, b) => {
    const chainCompare = a.chainId - b.chainId
    if (chainCompare !== 0) return chainCompare
    return a.contractAddress
      .toLowerCase()
      .localeCompare(b.contractAddress.toLowerCase())
  })
  return [
    QUERY_KEYS.NFT_COLLECTION_METADATA,
    'batch',
    sortedTargets
      .map((t) => `${t.chainId}-${t.contractAddress.toLowerCase()}`)
      .join(','),
  ]
}

/**
 * Fetches NFT collection metadata for multiple contracts from the backend.
 */
const fetchCollectionMetadataBatch = async (
  serverGetRequest: ServerGetRequestFunction,
  targets: CollectionMetadataTarget[],
): Promise<NFTCollectionMetadataLookup> => {
  if (targets.length === 0) {
    return new Map()
  }

  const contractsParam = targets.map((t) => ({
    chainId: t.chainId,
    address: t.contractAddress,
  }))

  const response = await serverGetRequest<BackendContractMetadataResponse>({
    path: '/api/metadata/nfts/contracts',
    params: {
      contracts: JSON.stringify(contractsParam),
    },
  })

  const lookup: NFTCollectionMetadataLookup = new Map()
  if (response) {
    response.data.contracts.forEach((contract) => {
      const key = getCollectionMetadataLookupKey(
        contract.chainId as SupportedChainId,
        contract.address as IAddress,
      )
      lookup.set(key, {
        contractAddress: EvmAddress.format(contract.address),
        chainId: contract.chainId as SupportedChainId,
        collectionName: contract.name ?? undefined,
      })
    })
  }

  return lookup
}

/**
 * Hook to fetch NFT collection metadata for a list of contract addresses.
 * Uses the backend endpoint which handles caching and external API routing.
 */
export const useNFTCollectionMetadata = (
  targets: CollectionMetadataTarget[],
): {
  lookup: NFTCollectionMetadataLookup
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()

  // Serialize targets for stable comparison - this ensures the same set of
  // contracts always uses the same cache key regardless of object identity
  const targetsKey = JSON.stringify(
    targets.map((t) => `${t.chainId}-${t.contractAddress.toLowerCase()}`),
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTargets = useMemo(() => targets, [targetsKey])

  const { data, isLoading } = useQuery({
    queryKey: buildBatchQueryKey(stableTargets),
    queryFn: () =>
      fetchCollectionMetadataBatch(serverGetRequest, stableTargets),
    enabled: stableTargets.length > 0,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })

  const lookup = useMemo((): NFTCollectionMetadataLookup => {
    if (!data) return new Map()
    if (data instanceof Map) return data
    // Handle case where cached data was serialized to a plain object (e.g., during SSR/hydration)
    return new Map(Object.entries(data))
  }, [data])

  return { lookup, isLoading }
}
